import { useMutation } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";

/**
 * Triggers the deal-items Excel export. Mirrors legacy
 * `Item/ShowItemExcel.aspx`. The endpoint streams the .xlsx back as a
 * binary blob; we trigger a browser download via an in-memory object
 * URL so the user gets the file without leaving the SPA.
 *
 * See audit 14-parity-matrix.md §5 item 34, audit 10 §A.1.
 */
export const useExportItemsExcel = (dealId: number) =>
  useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.get(
        `${APIS.Deals}/${dealId}/items.xlsx`,
        { responseType: "blob" }
      );

      // Pull the filename from Content-Disposition when present (the
      // server sets it to "Deal_{id}_Items_{date}.xlsx"); fall back to
      // a sensible default if the header is missing.
      const disposition = response.headers["content-disposition"] as string | undefined;
      const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i);
      const filename = match?.[1] ?? `Deal_${dealId}_Items.xlsx`;

      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        // Free the object URL on the next tick so the browser has
        // finished the download trigger before we revoke it.
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
    },
  });
